// The series wizard keeps its fee settings in local state and autosaves them
// with the rest of the plan. These stores stand in for the confirm's live
// endpoints so EventGeneralLedgerSettings and FeeRuleTable can edit that state.
// The server re-checks everything on save (EventSeries#replace_planned_fee_settings!).

export const BLANK_FEES = { generalLedgerCodeId: null, feeComponents: [], feeRuleOverrides: [] };

let counter = 0;
const newId = () => `new-fee-${(counter += 1)}`;

const refused = (message) => Promise.reject({ response: { data: [message] } });

const updater = (feesRef, onChange) => (patch) => {
  feesRef.current = { ...feesRef.current, ...patch };
  onChange(feesRef.current);
  return Promise.resolve();
};

export const seriesGeneralLedgerStore = (feesRef, onChange) => {
  const update = updater(feesRef, onChange);
  const components = () => feesRef.current.feeComponents || [];
  return {
    listComponents: () => Promise.resolve(components()),
    createComponent: (attributes) => {
      const name = (attributes.name || "").trim();
      const amount = parseFloat(attributes.amount);
      if (!name) return refused("Name can't be blank");
      if (Number.isNaN(amount) || amount < 0) return refused("Amount must be greater than or equal to 0");
      if (components().some((component) => component.name.toLowerCase() === name.toLowerCase())) {
        return refused("Name has already been taken");
      }
      return update({ feeComponents: [...components(), { ...attributes, name, id: newId() }] });
    },
    destroyComponent: (component) => update({ feeComponents: components().filter((row) => row.id !== component.id) }),
    setAccountCode: (value) => update({ generalLedgerCodeId: value === "" || value == null ? null : Number(value) }),
  };
};

const outOfRange = (changes) => {
  const fee = changes.organizer_service_fee;
  const percentage = changes.organizer_service_fee_percentage;
  if (fee != null && fee < 0) return "Organizer service fee must be greater than or equal to 0";
  if (percentage != null && (percentage < 0 || percentage > 100)) return "Organizer service fee percentage must be between 0 and 100";
  return null;
};

export const seriesFeeRuleOverrideStore = (feesRef, onChange) => {
  const update = updater(feesRef, onChange);
  const overrides = () => feesRef.current.feeRuleOverrides || [];
  return {
    listOverrides: () => Promise.resolve(overrides()),
    // Upserts by fee rule as a fallback: the table passes the stored override
    // when there is one, so this only guards against a stale caller.
    saveOverride: (existing, feeRuleId, changes) => {
      const problem = outOfRange(changes);
      if (problem) return refused(problem);
      const current = existing || overrides().find((row) => row.fee_rule_id === Number(feeRuleId));
      if (current) {
        return update({ feeRuleOverrides: overrides().map((row) => (row.id === current.id ? { ...row, ...changes } : row)) });
      }
      const created = { id: newId(), fee_rule_id: Number(feeRuleId), organizer_service_fee: null, organizer_service_fee_percentage: null, ...changes };
      return update({ feeRuleOverrides: [...overrides(), created] });
    },
    destroyOverride: (id) => update({ feeRuleOverrides: overrides().filter((row) => row.id !== id) }),
  };
};

export const feeComponentRows = (fees = BLANK_FEES) =>
  (fees.feeComponents || []).map((component, index) => ({
    name: component.name,
    amount: component.amount,
    general_ledger_code_id: component.general_ledger_code_id || null,
    position: index + 1,
  }));

export const feeRuleOverrideRows = (fees = BLANK_FEES) =>
  (fees.feeRuleOverrides || []).map((override) => ({
    fee_rule_id: override.fee_rule_id,
    organizer_service_fee: override.organizer_service_fee ?? null,
    organizer_service_fee_percentage: override.organizer_service_fee_percentage ?? null,
  }));
